import { AddType, Button, ClickEvent, DataSource, DataSourceMode, DataSourceModeChangeEvent, EditRowDecorator, Layout, TableRow, TableRowCreationEvent, TableRowDisplayEvent, ValidationResult } from "@mcleod/components";

import { OfficeRecurringPayAdjustment } from "./OfficeRecurringPayAdjustment";
import { Api, ModelRow } from "@mcleod/core";
import { AutogenLayoutOfficeRecurringPayAdjustmentTable } from "./autogen/AutogenLayoutOfficeRecurringPayAdjustmentTable";
import { ValidationUtil } from "@mcleod/components/src/databinding/ValidationUtil";

export class OfficeRecurringPayAdjustmentTable extends AutogenLayoutOfficeRecurringPayAdjustmentTable {
    private _payeeDataSource: DataSource;

    onLoad(): void | Promise<void> {
        this.tableRecurringPayAdjustments.allowEdit = false;
        this.tableRecurringPayAdjustments.onEditLayoutLoaded = this.configureEditLayout();
        this.mainDataSource.addAfterModeChangeListener((event: DataSourceModeChangeEvent) => {
            if (event.newMode === DataSourceMode.SEARCH) {
                this.tableRecurringPayAdjustments.allowEdit = true;
                this.tableRecurringPayAdjustments.addType = AddType.QUICK;
                this.tableRecurringPayAdjustments.displayToolsCell = false;
            }
        });
        this.tableRecurringPayAdjustments.addRowDisplayListener((event: TableRowDisplayEvent) => {
            if (this.mainDataSource.mode === DataSourceMode.SEARCH) {
                const tableRow = event.target as TableRow;
                tableRow.setAdditionalToolProps("buttonDelete", { visible: false });
            }
        });
    }

    get payeeDataSource(): DataSource {
        return this._payeeDataSource;
    }

    set payeeDataSource(value: DataSource) {
        this._payeeDataSource = value;
    }

    configureEditLayout(): (rowDecorator: EditRowDecorator, tableRow: TableRow) => void {
        return async (rowDecorator: EditRowDecorator, tableRow: TableRow) => {
            const layout = rowDecorator.layout as OfficeRecurringPayAdjustment;
            layout.mainDataSource.mode = DataSourceMode.UPDATE;
            const modelRow = tableRow.data as ModelRow;
            layout.textboxPayeeId.enabled = false;
            layout.activeRow.setValueAndLookupModelData("payee_id", this.payeeDataSource.activeRow?.get("id"), {
                id: this.payeeDataSource.activeRow?.get("id"),
                name: this.payeeDataSource.activeRow?.get("name")
            });
            if (this.payeeDataSource.mode === DataSourceMode.ADD) {
                layout.textboxPayeeId.enabled = false;
                layout.textboxPayeeId.required = false;
            } else {
                await layout.sourceComments.search({
                    parent_row_id: modelRow.getString("id"),
                    parent_row_type: "$"
                });
                layout.layoutTableComment.tableComments.addRowCreateListener((event: TableRowCreationEvent) => {
                    const row = event.getTableRow();
                    row.data.set("parent_row_id", modelRow.getString("id"));
                    row.data.set("parent_row_type", "$");
                });
                layout.sourceComments.mode = DataSourceMode.UPDATE;
            }
            rowDecorator.onSave = ((updatedData: ModelRow) => {
                tableRow.saveChangesFromEdit(updatedData);
                tableRow.displayComponentValues();
                if (layout.sourceComments.mode === DataSourceMode.UPDATE) {
                    layout.sourceComments.data.forEach(row => {
                        row.post();
                    });
                    layout.sourceComments.deletedData?.forEach(row => {
                        row.delete();
                    });
                }
            });
            rowDecorator.onValidate = ((updatedData: ModelRow ) => {
                return this.validateLayout(layout);
            });
        };
    }

    buttonDeleteOnClick(event: ClickEvent) {
        const button = event.target as Button;
        const tableRow = this.tableRecurringPayAdjustments.findTableRowFromAdditionalTool(button);
        if (tableRow.data._appending) {
            tableRow.deleteRow();
        } else {
            Api.search("lme/settlement/officepayroll/can-delete-pay-adjustment", { pay_adjustment_id: tableRow.data?.get("id") }).then(response => {
                tableRow.deleteRow();
            });
        }
    }

    buttonEditOnClick(event: ClickEvent) {
        const button = event.target as Button;
        const tableRow = this.tableRecurringPayAdjustments.findTableRowFromAdditionalTool(button);
        tableRow.showEditLayout()
    }

    async buttonAddRecurringPayAdjustmentOnClick(event: ClickEvent) {
        const startingData = await this.tableRecurringPayAdjustments._createNewRowData();
        startingData.setValueAndLookupModelData("payee_id", this.payeeDataSource.activeRow?.get("id"), {
            id: this.payeeDataSource.activeRow?.get("id"),
            name: this.payeeDataSource.activeRow?.get("name")
        });
        startingData.setValueAndLookupModelData("currency", this.payeeDataSource.activeRow?.get("currency"), this.payeeDataSource.activeRow?.getLookupModelData("currency"));
        const layout = Layout.getLayout(this.tableRecurringPayAdjustments.editLayout) as OfficeRecurringPayAdjustment;
        const erd = new EditRowDecorator({
            layout: layout,
            title: "Add Office Recurring Pay Adjustment",
            layoutLoadListeners: () => {
                layout.textboxPayeeId.enabled = false;
                layout.textboxPayeeId.required = false;
                layout.tabComments.visible = false;
            },
            width: this.tableRecurringPayAdjustments.editLayoutWidth,
            overlayProps: { closeOnClickOff: false, greyedBackground: true },
            data: startingData,
            dataSourceMode: DataSourceMode.ADD,
            onDelete: null,
            onSave: (updatedData: ModelRow | any) => {
                this.tableRecurringPayAdjustments.dataSource.addRow(updatedData, 0);
            },
            onValidate: (updatedData: ModelRow ) => {
                return this.validateLayout(layout);
            }
        });
    }

    validateLayout(layout: OfficeRecurringPayAdjustment): ValidationResult[] {
        const validationResults: ValidationResult[] = layout.mainDataSource.componentValidator.validate();
        if (validationResults && validationResults.length > 0) {
            ValidationUtil.showFailedValidationDialog(validationResults, "Unable to save this record because data is missing or invalid.");
        }
        return validationResults;
    }

    TableRowOnDblClick(event: ClickEvent) {
        const tableRow = event.target as TableRow;
        tableRow.showEditLayout();
    }
}
